import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createMerchant } from '../api/merchants'
import { getLocation } from '../utils/geolocation'

const SHOP_CATEGORIES = [
  'Electronics',
  'Clothing & Apparel',
  'Food & Groceries',
  'Home & Furniture',
  'Beauty & Health',
  'Sports & Outdoors',
  'Books & Stationery',
  'Toys & Kids',
  'Automotive',
  'Other',
] as const

const inputStyle = {
  background: 'var(--app-card-bg)',
  color: 'var(--tg-theme-text-color)',
  border: '1px solid transparent',
  borderRadius: '12px',
  padding: '10px 14px',
  width: '100%',
  fontSize: '16px',
  outline: 'none',
}

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  opacity: 0.5,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--tg-theme-text-color)',
  marginBottom: '6px',
  display: 'block',
}

export default function CreateShopPage() {
  const { t } = useTranslation('worker')
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [address, setAddress] = useState('')
  const [category, setCategory] = useState('')
  const [description, setDescription] = useState('')
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGps = async () => {
    setGpsLoading(true)
    setGpsError(null)
    try {
      const { latitude, longitude } = await getLocation()
      setLat(latitude)
      setLng(longitude)
    } catch (e: unknown) {
      setGpsError(e instanceof Error ? e.message : t('common:error.failedToLoad', { ns: 'common' }))
    } finally {
      setGpsLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!name.trim()) return
    setIsSubmitting(true)
    setError(null)
    try {
      const merchant = await createMerchant({
        shop_name: name.trim(),
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        category: category || undefined,
        description: description.trim() || undefined,
        latitude: lat ?? undefined,
        longitude: lng ?? undefined,
      })
      navigate(`/shops/${merchant.id}`)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('common:error.generic', { ns: 'common' }))
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen pb-28 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      <div className="p-4 space-y-4">
        <button
          className="text-sm opacity-60"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(-1)}
        >
          {t('common:back', { ns: 'common' })}
        </button>

        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('createShop.title')}
          </h1>
          <p className="text-sm opacity-60 mt-1" style={{ color: 'var(--tg-theme-text-color)' }}>
            {t('createShop.subtitle')}
          </p>
        </div>

        {/* Name */}
        <div>
          <label style={labelStyle}>{t('createShop.nameLabel')}</label>
          <input
            style={inputStyle}
            placeholder={t('createShop.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        {/* Phone */}
        <div>
          <label style={labelStyle}>{t('createShop.phoneLabel')}</label>
          <input
            style={inputStyle}
            type="tel"
            placeholder={t('createShop.phonePlaceholder')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        {/* Address */}
        <div>
          <label style={labelStyle}>{t('createShop.addressLabel')}</label>
          <input
            style={inputStyle}
            placeholder={t('createShop.addressPlaceholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        {/* Category */}
        <div>
          <label style={labelStyle}>{t('createShop.categoryLabel')}</label>
          <select
            style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">{t('createShop.selectCategory')}</option>
            {SHOP_CATEGORIES.map((c) => (
              <option key={c} value={c}>{t(`shops.categories.${c}`)}</option>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>{t('createShop.descriptionLabel')}</label>
          <textarea
            style={{ ...inputStyle, minHeight: '72px', resize: 'none' as const }}
            placeholder={t('createShop.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* GPS */}
        <div>
          <label style={labelStyle}>{t('createShop.gpsLabel')}</label>
          <button
            className="w-full py-2.5 rounded-xl text-sm font-medium"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-link-color)' }}
            onClick={handleGps}
            disabled={gpsLoading}
          >
            {gpsLoading ? t('createShop.gpsLoading') : lat != null ? t('createShop.gpsRecapture') : t('createShop.gpsCapture')}
          </button>
          {lat != null && lng != null && (
            <p className="text-xs text-center mt-1.5 opacity-60" style={{ color: 'var(--tg-theme-text-color)' }}>
              📍 {lat.toFixed(5)}, {lng.toFixed(5)}
            </p>
          )}
          {gpsError && (
            <p className="text-xs text-center mt-1" style={{ color: '#ef4444' }}>{gpsError}</p>
          )}
        </div>

        <p className="text-xs opacity-40 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('createShop.reviewNote')}
        </p>

        {error && (
          <div
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: '#ef444418', color: '#ef4444' }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Submit */}
      <div
        className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom"
        style={{ background: 'var(--app-page-bg)' }}
      >
        <button
          className="w-full py-3 rounded-2xl font-semibold text-base disabled:opacity-40"
          style={{
            background: 'var(--tg-theme-button-color)',
            color: 'var(--tg-theme-button-text-color)',
          }}
          disabled={!name.trim() || isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? t('createShop.registering') : t('createShop.register')}
        </button>
      </div>
    </div>
  )
}
